import React, { useState, useRef, useEffect, useCallback } from "react";
import "./searchHeader.css";
import { FaSearch, FaUser } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Menu, MenuItem } from "@mui/material";
import { resetClientInfo } from "../../redux/clientSlice";
import { resetClientData, updateStatus } from "../../redux/clientDataSlice";
import { persistor } from "../../redux/store";
import { supabase } from "../../supabaseClient"; // Import Supabase client
import QuidproLogo from "./assets/quidproLowerLogo.png";
import iconSearch from "../../home/new/assets/icon-search.png";
import iconLocation from "../../home/new/assets/icon-location.png";
import iconPractice from "../../home/new/assets/icon-practice.png";
import WhiteSearchIcon from "../../home/new/assets/white-search-icon.png";
import DownIcon from "./assets/downIcon.png";
import BlueDropdown from "./assets/blueDropdown.png";
import NotificationIcon from "./assets/notificationIcon.png";
import PracticeAreaPicker from "../../home/new/components/practiceAreaPicker";
import CustomCompleteBox from "../../home/new/components/customCompleteBox";
import debounce from "lodash.debounce";
import QPLogo from "./assets/qpLogo.png";
import { getPracticeAreaLabel } from "../../helpers/stringHelper";

const SearchHeader = ({
  openLoginPopup,
  title,
  isTitle,
  headerColor,
  searchBar = true, // Default value is true, can be overridden by parent
  initialName = "",
  initialPracticeArea = "",
  initialLatitude,
  initialLongitude,
  initialRadius,
  initialLocation,
}) => {
  const searchMenuItemStyles = {
    fontFamily: "Poppins",
    fontSize: "16px",
    color: "#333333",
    fontWeight: 400,
    lineHeight: "100%",
    letterSpacing: "-0.17px",
    padding: "15px 20px", // Ensure adequate padding
    border: "2px solid transparent", // Transparent by default
    borderRadius: "4px", // Optional: soften corners
    transition: "border 0.2s ease, background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "#fff", // or "#e0e0e0", depending on the look you want
      border: "2px solid #144A8C", // Blue border on hover
    },
  };

  const menuItemStylesAuth = () => ({
    fontFamily: "Poppins",
    fontWeight: 400,
    fontSize: "18px",
    lineHeight: "130%",
    letterSpacing: "-0.17px",
    textAlign: "center",
    verticalAlign: "middle",
    color: "#333333",
    px: "30px",
    pr: "48px",
    py: "16px",
    mx: "20px",
    my: "10px",
    borderRadius: "8px",
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [searchName, setSearchName] = useState("");
  const [locationInput, setLocationInput] = useState(initialLocation);
  const [autocompleteResults, setAutocompleteResults] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [locationError, setLocationError] = useState("");
  const geocodeCache = useRef({});
  const [practiceArea, setPracticeArea] = useState(() => ({
    slug: initialPracticeArea || "",
    label: initialPracticeArea ? getPracticeAreaLabel(initialPracticeArea) : "",
  }));
  const [latitude, setLatitude] = useState(initialLatitude);
  const [longitude, setLongitude] = useState(initialLongitude);
  const [geolocationError, setGeolocationError] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const AUTOCOMPLETE_API =
    "https://autocomplete.search.hereapi.com/v1/autocomplete";
  const HERE_API_KEY = process.env.REACT_APP_HERE_API_KEY;

  const status = useSelector((state) => state.clientData.status);
  const name = useSelector((state) => {
    const client = state.clientData.clientData;
    return client
      ? `${client.first_name || ""} ${client.last_name || ""}`.trim()
      : "";
  });  const profilePhotoUrl = useSelector(
    (state) => state.clientData.clientData?.profile_photo_url
  );

  const handleMenuClick = (event) => setMenuAnchor(event.currentTarget);
  const handleMenuClose = () => setMenuAnchor(null);

  const handleLogoClick = () => {
    navigate("/");
  };

  const debouncedFetchLocation = useCallback(
    debounce(async (q) => {
      if (q) {
        if (q.length < 3) {
          setAutocompleteResults([]);
          return;
        }
      }

      setIsLoadingLocation(true);
      setLocationError("");
      try {
        const url = `${AUTOCOMPLETE_API}?${new URLSearchParams({
          q,
          apiKey: HERE_API_KEY,
        })}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(res.statusText);
        const { items } = await res.json();
        setAutocompleteResults(items || []);
      } catch (e) {
        console.error(e);
        setLocationError("Failed to load suggestions.");
      } finally {
        setIsLoadingLocation(false);
      }
    }, 300),
    []
  );

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      dispatch(resetClientInfo());
      dispatch(resetClientData());
      dispatch(updateStatus("idle"));
      await persistor.purge();

      navigate("/"); // Redirect to home page after logout
    } catch (error) {
      console.error("Error logging out:", error.message);
      alert("Failed to log out. Please try again.");
    } finally {
      handleMenuClose(); // Close the menu after logout
    }
  };

  // Determine the header class based on headerColor prop
  const headerClass = headerColor
    ? `search-header ${headerColor}`
    : "search-header";

  const handleSearch = () => {
    // 1) If user picked from autocomplete, we have coords in state
    if (latitude && longitude) {
      return doNavigate(latitude, longitude);
    }
    // 2) Otherwise use initial props if they exist
    if (initialLatitude && initialLongitude) {
      return doNavigate(initialLatitude, initialLongitude);
    }
    // 3) Fallback to navigator.geolocation (or alert)
    if (navigator.geolocation) {
      return navigator.geolocation.getCurrentPosition(
        (pos) => doNavigate(pos.coords.latitude, pos.coords.longitude),
        () => alert("Please allow location access or enter a location.")
      );
    }
    alert("Please enter a location to proceed.");
  };

  function doNavigate(lat, lon) {
    const params = new URLSearchParams();

    if (searchName.trim()) {
      params.set("name", searchName.trim());
    }

    if (practiceArea.slug) {
      params.set("practice_area", practiceArea.slug);
    }

    if (lat != null && lon != null) {
      params.set("latitude", lat);
      params.set("longitude", lon);
    }

    if (initialRadius) {
      params.set("radius", initialRadius);
    }

    // only include location when there's something to send
    const loc = selectedLocation?.title || locationInput;
    if (loc && loc.trim()) {
      params.set("location", loc.trim());
    }

    navigate(`/search-results?${params.toString()}`);
  }

  useEffect(() => {
    debouncedFetchLocation(locationInput);
    return debouncedFetchLocation.cancel;
  }, [locationInput, debouncedFetchLocation]);

  const handleSelectLocation = async (suggestion) => {
    const { latitude: newLat, longitude: newLon } = await fetchGeocode(
      suggestion.title
    );
    setLocationInput(suggestion.title);
    setLatitude(newLat);
    setLongitude(newLon);
    setAutocompleteResults([]);
    doNavigate(newLat, newLon);
  };

  const fetchGeocode = async (address) => {
    if (geocodeCache.current[address]) {
      return geocodeCache.current[address];
    }
    try {
      const res = await fetch(
        `https://geocode.search.hereapi.com/v1/geocode?${new URLSearchParams({
          q: address,
          apiKey: process.env.REACT_APP_HERE_API_KEY,
        })}`
      );
      if (!res.ok) throw new Error(res.statusText);
      const { items } = await res.json();
      if (items?.length) {
        const { lat, lng } = items[0].position;
        const coords = { latitude: lat, longitude: lng };
        geocodeCache.current[address] = coords;
        return coords;
      }
    } catch (e) {
      console.error(e);
    }
    // fallback to default if geocode fails
    return { latitude: initialLatitude, longitude: initialLongitude };
  };

  return (
    <div>
      <div className={headerClass}>
        <div className="search-header-left">
          {isTitle ? (
            <h1 className="search-header-title">{title}</h1>
          ) : (
            <img
              src={QPLogo}
              className="quidpro-search-results-logo"
              alt="Quidpro Logo"
              onClick={handleLogoClick}
            />
          )}
        </div>

        {/* Conditionally render the search bar */}
        {searchBar && (
          <div className="results__search-container">
            {/* The "box" portion that holds all three inputs + icons */}
            <div className="results__search-box">
              <div className="search__field">
                <img
                  src={iconSearch}
                  alt="Search Icon"
                  className="results__search__icon"
                />
                <input
                  type="text"
                  placeholder={
                    initialName?.trim()
                      ? initialName
                      : "Search by name, keyword..."
                  }
                  className="results_search__input"
                  value={initialName?.trim() ? initialName : searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                />
              </div>

              <div className="results__divider"></div>

              {/* <div className="search__field">
                <img
                  src={iconLocation}
                  alt="Location Icon"
                  className="results__search__icon"
                />
                <input
                  type="text"
                  placeholder={
                    initialLocation.trim()
                      ? initialLocation
                      : "City, state, or zip code"
                  }
                  className="results_search__input"
                  value={locationInput}
                  onChange={(e) => setLocationInput(e.target.value)}
                />
              </div> */}

              <div className="search__field">
                <img
                  src={iconLocation}
                  className="results__search__icon"
                  alt=""
                />
                <CustomCompleteBox
                  useSearchStyle={true}
                  placeholder={
                    initialLocation?.trim()
                      ? initialLocation
                      : "City, state, or zip code"
                  }
                  options={autocompleteResults}
                  value={selectedLocation}
                  inputValue={locationInput}
                  onInputChange={(val) => {
                    setLocationInput(val);
                    setSelectedLocation(null);
                  }}
                  onChange={handleSelectLocation}
                  getOptionLabel={(item) => item.title}
                  isLoading={isLoadingLocation}
                  error={locationError}
                />
              </div>

              <div className="results__divider"></div>

              <div className="search__field">
                <img
                  src={iconPractice}
                  alt="Practice Area Icon"
                  className="results__search__icon"
                />
                <input
                  type="text"
                  className="results_search__input"
                  placeholder="Practice area"
                  value={practiceArea.label || ""}
                  readOnly
                  onClick={() => setShowPicker(true)}
                />
              </div>
            </div>

            {/* The standalone "Search" button to the right */}
            <button className="results__search__button" onClick={handleSearch}>
              <img
                src={WhiteSearchIcon}
                alt="search-icon"
                style={{ width: "24px", height: "24px" }}
              />
            </button>
          </div>
        )}

        <div className="search-header-right">
          {status === "online" ? (
            // Render this if the client is online
            <div style={{ display: "flex", alignItems: "center" }}>
              <div className="search-login-dropdown">
                <img
                  src={NotificationIcon}
                  style={{ width: "24px", height: "24px" }}
                  alt="dropdown-icon"
                />
              </div>
              <div className="search-login-dropdown" onClick={handleMenuClick}>
                <p className="logged-in-name">{name}</p>
                <img
                  src={DownIcon}
                  style={{ width: "24px", height: "24px" }}
                  alt="dropdown-icon"
                />
              </div>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 50,
                  horizontal: 120,
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                MenuListProps={{
                  sx: {
                    borderRadius: 2,
                    minWidth: 180,
                  },
                }}
              >
                <MenuItem
                  onClick={() => navigate("/client/my-appointments")}
                  sx={menuItemStylesAuth()}
                >
                  My Appointments
                </MenuItem>

                <MenuItem
                  onClick={() => navigate("/client/my-profile")}
                  sx={menuItemStylesAuth()}
                >
                  My Profile
                </MenuItem>

                <MenuItem sx={menuItemStylesAuth()}>Settings</MenuItem>
                <hr style={{ width: "80%", justifySelf: "center" }} />
                <MenuItem onClick={handleLogout} sx={menuItemStylesAuth()}>
                  Logout
                </MenuItem>
              </Menu>
            </div>
          ) : (
            // If not logged in, show the login/signup options
            <div className="home-login-signup">
              <div className="home-login-dropdown" onClick={handleMenuClick}>
                Log in
                <img
                  src={BlueDropdown}
                  style={{ width: "24px", height: "24px" }}
                  alt="dropdown-icon"
                />
              </div>

              <div
                className="home-signup-button"
                onClick={() => {
                  navigate("/createuser/auth");
                }}
              >
                Sign up
              </div>

              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 50,
                  horizontal: -200,
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                //   ref={menuRef}
                MenuListProps={{
                  sx: {
                    minWidth: 330,
                    minHeight: 290,
                    gap: "20px",
                    borderRadius: "8px",
                    display: "flex",
                    justifyContent: "center",
                    flexDirection: "column",
                    paddingLeft: 3,
                    paddingRight: 3,
                    boxShadow: "8px 8px 53.5px 0px #00000014",
                  },
                }}
              >
                <p className="login-menu-title">Clients</p>
                <MenuItem
                  onClick={() => {
                    openLoginPopup(); // Open login popup
                    handleMenuClose();
                  }}
                  sx={searchMenuItemStyles}
                >
                  Log in
                </MenuItem>

                <hr style={{ width: "100%", justifySelf: "center" }} />

                <p className="login-menu-title">Lawyers</p>
                <MenuItem
                  onClick={() => {
                    navigate("/grow/email");
                    handleMenuClose();
                  }}
                  sx={searchMenuItemStyles}
                >
                  Log in
                </MenuItem>
              </Menu>
            </div>
          )}
        </div>
      </div>
      <PracticeAreaPicker
        open={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={(choice) => setPracticeArea(choice)}
      />
    </div>
  );
};

export default SearchHeader;
